import { Checkbox, DatePicker, Form, Radio, Space, type FormInstance } from "antd";
import { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { type SentTimeConfig } from "~/modules/Automation/AutomatedMessagesForm/types";

export type TimeFieldProps = {
  showOnlyAbsolute?: boolean;
  /** Form field holding the picked time (default "sentTime"). */
  name?: string;
  /** Field label (default the automated-message sent-time label). */
  label?: string;
  formController?: FormInstance<any> &
    FormInstance<{
      sentTime: Dayjs;
      sentTimeConfig?: SentTimeConfig;
    }>;
};

export const DISABLED_HOURS = [0, 1, 2, 3, 4, 5, 6, 23];

const validatePickerTime = () => DISABLED_HOURS;

export const TimeField = ({ showOnlyAbsolute = true, name = "sentTime", label }: TimeFieldProps) => {
  const { t } = useTranslation(["dashboard", "common", "products"]);
  const fieldLabel = label ?? t("automatedMessageForm.sentTime");

  if (showOnlyAbsolute) {
    return (
      <Form.Item
        name={name}
        label={fieldLabel}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <DatePicker.TimePicker
          minuteStep={15}
          allowClear={false}
          format="HH:mm"
          disabledHours={validatePickerTime}
          placeholder={t<string>("automatedMessageForm.timePlaceholder.absolute")}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item label={fieldLabel}>
      <Form.Item
        name={["sentTimeConfig", "mode"]}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
        noStyle
      >
        <Radio.Group>
          <Space direction="vertical">
            <Radio value="absolute">{t("automatedMessageForm.timeMode.absolute")}</Radio>
            <Radio value="relative">{t("automatedMessageForm.timeMode.relative")}</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        noStyle
        dependencies={[
          ["sentTimeConfig", "mode"],
          ["sentTimeConfig", "avoidSendingOnTheNight"],
        ]}
      >
        {({ getFieldValue }) => {
          const mode = (getFieldValue(["sentTimeConfig", "mode"]) as "absolute" | "relative") || "absolute";
          const avoidSendingOnTheNight = getFieldValue(["sentTimeConfig", "avoidSendingOnTheNight"]) as boolean;
          return (
            <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
              <Space direction="horizontal" align="start">
                <Form.Item
                  name={name}
                  rules={[
                    {
                      required: true,
                      message: t<string>("common:validationErrors.fieldIsRequired"),
                    },
                  ]}
                  help={
                    mode === "relative" ? (
                      <span style={{ color: "#8c8c8c", fontSize: "12px", maxWidth: "200px" }}>
                        {t("automatedMessageForm.timeMode.relativeHint")}
                      </span>
                    ) : null
                  }
                >
                  <DatePicker.TimePicker
                    minuteStep={5}
                    allowClear={false}
                    format="HH:mm"
                    disabledHours={mode === "absolute" ? validatePickerTime : undefined}
                    placeholder={
                      mode === "relative"
                        ? t<string>("automatedMessageForm.timePlaceholder.relative")
                        : t<string>("automatedMessageForm.timePlaceholder.absolute")
                    }
                  />
                </Form.Item>
              </Space>

              {mode === "relative" && (
                <Space direction="vertical">
                  <Form.Item name={["sentTimeConfig", "avoidSendingOnTheNight"]} valuePropName="checked" noStyle>
                    <Checkbox>{t("automatedMessageForm.avoidSendingAtNight")}</Checkbox>
                  </Form.Item>

                  {avoidSendingOnTheNight && (
                    <Form.Item
                      name={["sentTimeConfig", "calculateFullRangeOverNight"]}
                      valuePropName="checked"
                      noStyle
                      style={{ marginLeft: 24 }}
                    >
                      <Checkbox>{t("automatedMessageForm.calculateFullRangeOverNight")}</Checkbox>
                    </Form.Item>
                  )}
                </Space>
              )}
            </Space>
          );
        }}
      </Form.Item>
    </Form.Item>
  );
};
